import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import "@/index.css";
import axios from "axios";
import { RefreshCw, Download, FileText, TrendingUp, Activity, Mail, LineChart, Info, LogIn, LogOut, Lock, KeyRound, Users, Share2 } from "lucide-react";
import FilterBar from "@/components/FilterBar";
import StockTable, { DEFAULT_VISIBLE_COLUMNS } from "@/components/StockTable";
import StatCard from "@/components/StatCard";
import EmailPanel from "@/components/EmailPanel";
import StockDetail from "@/components/StockDetail";
import SectorHeatmap from "@/components/SectorHeatmap";
import ColumnSelector from "@/components/ColumnSelector";
import About from "@/components/About";
import Contact from "@/components/Contact";
import Services from "@/components/Services";
import ScreenerInfo from "@/components/ScreenerInfo";
import LoginModal from "@/components/LoginModal";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import UsersPanel from "@/components/UsersPanel";
import ResetPasswordModal from "@/components/ResetPasswordModal";
import SharePanel from "@/components/SharePanel";
import { fmtM, fmtPct, fmtNum } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function App() {
  const [status, setStatus] = useState({ status: "idle", done: 0, total: 58 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [qualifiedOnly, setQualifiedOnly] = useState(true);
  const [showEmail, setShowEmail] = useState(false);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState("about");
  const [visitorStats, setVisitorStats] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("auth_token"));
  const [authEmail, setAuthEmail] = useState(() => localStorage.getItem("auth_email"));
  const [showLogin, setShowLogin] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [isSuper, setIsSuper] = useState(() => localStorage.getItem("auth_is_super") === "1");
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get("reset_token"));
  const [authMsg, setAuthMsg] = useState(null);
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("desc");
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("visible_columns"));
      if (Array.isArray(saved) && saved.length) return saved;
    } catch { /* ignore */ }
    return DEFAULT_VISIBLE_COLUMNS;
  });
  useEffect(() => {
    localStorage.setItem("visible_columns", JSON.stringify(visibleColumns));
  }, [visibleColumns]);
  const DEFAULT_FILTERS = {
    sector: "", min_mcap_b: 10, max_mcap_b: "", max_pe: "", max_payout: "",
    min_rev_growth: "", min_op_margin: "", min_net_margin: "", min_ebitda_margin: "",
    max_pe_vs_peer: "", min_annual_growth: "", require_quarterly_growth: false,
    max_per_sector: "",
  };
  const [filters, setFilters] = useState(DEFAULT_FILTERS);       // draft
  const [period, setPeriod] = useState("quarterly");             // draft basis
  const [applied, setApplied] = useState({ filters: DEFAULT_FILTERS, period: "quarterly" });
  const pollRef = useRef(null);

  const dirty = useMemo(
    () => JSON.stringify({ f: filters, p: period }) !== JSON.stringify({ f: applied.filters, p: applied.period }),
    [filters, period, applied]
  );

  // Unique visitor tracking (privacy-friendly: random id in localStorage)
  useEffect(() => {
    let vid = localStorage.getItem("visitor_id");
    if (!vid) {
      vid = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random();
      localStorage.setItem("visitor_id", vid);
    }
    axios.post(`${API}/visit`, { visitor_id: vid })
      .then((r) => setVisitorStats(r.data))
      .catch(() => {});
  }, []);

  // Magic-link login: verify token from URL (?login_token=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const lt = params.get("login_token");
    if (!lt) return;
    axios.post(`${API}/auth/magic/verify`, { token: lt })
      .then((r) => {
        localStorage.setItem("auth_token", r.data.token);
        localStorage.setItem("auth_email", r.data.email);
        localStorage.setItem("auth_is_super", r.data.is_super ? "1" : "0");
        setToken(r.data.token);
        setAuthEmail(r.data.email);
        setIsSuper(!!r.data.is_super);
        setAuthMsg({ type: "ok", text: `Signed in as ${r.data.email}` });
      })
      .catch((e) => {
        setAuthMsg({ type: "err", text: e.response?.data?.detail || "Login link invalid or expired." });
      })
      .finally(() => {
        params.delete("login_token");
        const qs = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
      });
  }, []);

  // Strip the reset_token from the URL once captured (modal handles it).
  useEffect(() => {
    if (!resetToken) return;
    const params = new URLSearchParams(window.location.search);
    params.delete("reset_token");
    const qs = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }, [resetToken]);

  // Refresh role/super + validate session on load.
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setIsSuper(!!r.data.is_super);
        localStorage.setItem("auth_is_super", r.data.is_super ? "1" : "0");
      })
      .catch((e) => {
        if (e.response?.status === 401 || e.response?.status === 403) logout();
      });
    // eslint-disable-next-line
  }, [token]);

  // Picks tab is admin-only; if logged out while on it, fall back to QLS tab.
  useEffect(() => {
    if (!token && activeTab === "picks") setActiveTab("about");
  }, [token, activeTab]);

  const fetchStocks = useCallback(async () => {
    if (!token) return;
    const f = applied.filters;
    const numOr = (v, d) => (v === "" || v === null || isNaN(parseFloat(v)) ? d : parseFloat(v));
    const params = {
      period: applied.period,
      min_mcap: numOr(f.min_mcap_b, 10) * 1e9,
      max_mcap: numOr(f.max_mcap_b, 0) * 1e9,
      max_pe: numOr(f.max_pe, 100000),
      max_payout: numOr(f.max_payout, 100000),
      min_rev_growth: numOr(f.min_rev_growth, 0),
      min_op_margin: numOr(f.min_op_margin, 0),
      min_net_margin: numOr(f.min_net_margin, 0),
      min_ebitda_margin: numOr(f.min_ebitda_margin, 0),
      max_pe_vs_peer: numOr(f.max_pe_vs_peer, 0),
      min_annual_growth: numOr(f.min_annual_growth, 0),
      require_quarterly_growth: !!f.require_quarterly_growth,
      max_per_sector: numOr(f.max_per_sector, 0),
    };
    const res = await axios.get(`${API}/stocks`, { params, headers: { Authorization: `Bearer ${token}` } })
      .catch((e) => {
        if (e.response?.status === 401) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("auth_email");
          setToken(null);
          setAuthEmail(null);
        }
        setLoading(false);
        return null;
      });
    if (!res) return;
    setData(res.data);
    setLoading(false);
  }, [token, applied]);

  const onApply = () => setApplied({ filters, period });

  const pollStatus = useCallback(async () => {
    const res = await axios.get(`${API}/status`);
    setStatus(res.data);
    if (res.data.status === "ready" && res.data.count > 0) {
      clearInterval(pollRef.current);
      setRefreshing(false);
      fetchStocks();
    }
  }, [fetchStocks]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    (async () => {
      const res = await axios.get(`${API}/status`);
      setStatus(res.data);
      if (res.data.status === "ready" && res.data.count > 0) {
        fetchStocks();
      } else {
        pollRef.current = setInterval(pollStatus, 2000);
      }
    })();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (token && status.status === "ready" && status.count > 0) {
      const t = setTimeout(fetchStocks, 400);
      return () => clearTimeout(t);
    }
  }, [fetchStocks, status.status, status.count, token]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await axios.post(`${API}/refresh`);
    pollRef.current = setInterval(pollStatus, 2000);
  };

  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const logout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_email");
    localStorage.removeItem("auth_is_super");
    setToken(null); setAuthEmail(null); setIsSuper(false);
  };

  const handleAuthed = (tok, email, superFlag, mustChange) => {
    localStorage.setItem("auth_token", tok);
    localStorage.setItem("auth_email", email);
    localStorage.setItem("auth_is_super", superFlag ? "1" : "0");
    setToken(tok);
    setAuthEmail(email);
    setIsSuper(!!superFlag);
    setAuthMsg({ type: "ok", text: `Signed in as ${email}` });
    if (mustChange) {
      setAuthMsg({ type: "ok", text: `Signed in as ${email}. Please set a new password.` });
      setShowChangePw(true);
    }
  };

  const requireAuth = (action) => {
    if (token) action();
    else setShowLogin(true);
  };

  const downloadFile = async (path, filename) => {
    const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.status === 401) { logout(); setShowLogin(true); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const rows = useMemo(() => {
    if (!data) return [];
    let list = [...data.stocks];
    if (qualifiedOnly) list = list.filter((s) => s.qualifies);
    if (filters.sector) list = list.filter((s) => s.sector === filters.sector);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((s) => s.ticker.toLowerCase().includes(q) || (s.name || "").toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      const av = a[sortKey] ?? -Infinity, bv = b[sortKey] ?? -Infinity;
      const cmp = typeof av === "string" || typeof bv === "string" ? String(av).localeCompare(String(bv)) : av - bv;
      return sortDir === "desc" ? -cmp : cmp;
    });
    return list;
  }, [data, qualifiedOnly, filters.sector, search, sortKey, sortDir]);

  const stats = useMemo(() => {
    if (!rows.length) return null;
    const yoys = rows.map((s) => s.ebitda_yoy).filter((v) => v != null);
    const avgYoy = yoys.reduce((a, b) => a + b, 0) / (yoys.length || 1);
    const totalBuyback = rows.reduce((a, b) => a + (b.buyback_12m_m || 0), 0);
    const beats = rows.filter((s) => s.beats_peers).length;
    return { avgYoy, totalBuyback, beats };
  }, [rows]);

  const lastUpdated = data?.last_updated
    ? new Date(data.last_updated).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : "\u2014";

  const screenerLoading = loading || status.status === "loading" || refreshing;

  const TabNav = () => (
    <div className="flex items-center justify-between gap-2 border-b border-[#D2E0E6] mb-6" data-testid="tab-nav">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
        <img src="/qls-lockup.png" alt="Quantum Leap Solutions" data-testid="nav-logo"
          className="h-7 sm:h-9 w-auto object-contain shrink-0" />
        <div className="relative min-w-0 flex-1">
        <div className="flex items-center gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {[
          ...(token ? [{ id: "picks", label: "Today's Stock Pick", icon: LineChart }] : []),
          { id: "about", label: "QLS", icon: Info },
          { id: "services", label: "Our Services", icon: LineChart },
          { id: "contact", label: "Contact Us", icon: Mail },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-2.5 sm:px-4 py-2.5 text-sm border-b-2 -mb-px whitespace-nowrap shrink-0 transition-colors ${
              activeTab === t.id
                ? "border-[#0284C7] text-[#0B2830] font-medium"
                : "border-transparent text-[#7C949C] hover:text-[#47616B]"
            }`}
          >
            <t.icon className="w-4 h-4 shrink-0" /> {t.label}
          </button>
        ))}
        </div>
        <div className="sm:hidden pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[#EDF3F6] to-transparent"></div>
        </div>
      </div>
      {token ? (
        <div className="flex items-center gap-3 pr-1 shrink-0">
          <span className="text-xs text-[#7C949C] mono hidden sm:inline" data-testid="auth-email">{authEmail}</span>
          <button data-testid="change-pw-btn" onClick={() => setShowChangePw(true)}
            className="flex items-center gap-1.5 text-xs text-[#47616B] hover:text-[#0284C7] transition-colors">
            <KeyRound className="w-4 h-4" /> <span className="hidden sm:inline">Password</span>
          </button>
          <button data-testid="logout-btn" onClick={logout}
            className="flex items-center gap-1.5 text-xs text-[#47616B] hover:text-[#E11D48] transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      ) : (
        <button data-testid="login-btn" onClick={() => setShowLogin(true)}
          className="flex items-center gap-1.5 text-xs text-[#47616B] hover:text-[#0284C7] transition-colors pr-1 shrink-0">
          <LogIn className="w-4 h-4" /> Login
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#EDF3F6] px-4 py-6 md:px-8">
      <TabNav />

      {authMsg && (
        <div data-testid="auth-msg" className={`mb-4 text-sm px-4 py-2.5 border rounded-md ${
          authMsg.type === "ok"
            ? "border-[#0D9488]/30 text-[#0D9488] bg-[#0D9488]/5"
            : "border-[#E11D48]/30 text-[#E11D48] bg-[#E11D48]/5"}`}>
          {authMsg.text}
        </div>
      )}

      {activeTab === "about" ? (
        <About />
      ) : activeTab === "contact" ? (
        <Contact />
      ) : activeTab === "services" ? (
        <Services />
      ) : !token ? (
        <div className="flex items-center justify-center px-6 py-24" data-testid="picks-auth-gate">
          <div className="w-full max-w-md text-center bg-white border border-[#D2E0E6] rounded-lg shadow-sm p-10 fade-up">
            <div className="w-12 h-12 mx-auto mb-5 flex items-center justify-center bg-[#0284C7]/10 border border-[#0284C7]/25 rounded-md">
              <Lock className="w-6 h-6 text-[#0284C7]" />
            </div>
            <h2 className="text-xl font-semibold mb-2 text-[#0B2830]">Today's Stock Pick is private</h2>
            <p className="text-sm text-[#47616B] mb-6">
              Sign in to view the ranked stock list and download the report/CSV. Admins can also manage viewers and the daily email.
            </p>
            <button data-testid="gate-login-btn" onClick={() => setShowLogin(true)}
              className="inline-flex items-center gap-2 bg-[#0284C7] text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-[#0369A1] transition-colors">
              <LogIn className="w-4 h-4" /> Login
            </button>
          </div>
        </div>
      ) : screenerLoading ? (
        <div className="flex items-center justify-center px-6 py-24">
          <div className="w-full max-w-md text-center">
            <Activity className="w-10 h-10 text-[#0284C7] mx-auto mb-6 animate-pulse" />
            <h2 className="text-xl font-semibold mb-2 text-[#0B2830]">Screening global markets</h2>
            <p className="text-sm text-[#47616B] mb-6">
              Pulling live fundamentals, EBITDA history & buybacks for {status.total} companies&hellip;
            </p>
            <div className="h-1.5 bg-[#E9F1F4] w-full overflow-hidden rounded-full">
              <div className="h-full bg-[#0284C7] transition-all duration-500"
                style={{ width: `${status.total ? Math.round((status.done / status.total) * 100) : 0}%` }} />
            </div>
            <div className="mono text-xs text-[#47616B] mt-3">
              {status.done} / {status.total} &middot; {status.total ? Math.round((status.done / status.total) * 100) : 0}%
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Header */}
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 text-[#0284C7] mb-1">
                <TrendingUp className="w-5 h-5" />
                <span className="mono text-xs uppercase tracking-[0.25em]">EBITDA Edge</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0B2830]">Global Value & Buyback Screener</h1>
              <p className="text-sm text-[#47616B] mt-1 max-w-2xl">
                All US-listed stocks (S&P 500) above $10B market cap with positive annual EBITDA growth &middot; screen by revenue growth, margins, P/E & buybacks.
                Toggle quarterly / annual basis. Data: stockanalysis.com &middot; Updated {lastUpdated}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ColumnSelector visibleColumns={visibleColumns} setVisibleColumns={setVisibleColumns} />
              <button data-testid="export-csv" onClick={() => requireAuth(() => downloadFile(`/export/csv?period=${applied.period}`, `stock_screen_${applied.period}.csv`))}
                className="flex items-center gap-1.5 bg-white border border-[#D2E0E6] text-[#47616B] px-3 py-2 rounded-md text-sm hover:bg-[#E0EBEF] hover:text-[#0B2830] transition-colors">
                <Download className="w-4 h-4" /> CSV
              </button>
              <button data-testid="export-report" onClick={() => requireAuth(() => downloadFile(`/export/report?period=${applied.period}`, `stock_screen_report_${applied.period}.md`))}
                className="flex items-center gap-1.5 bg-white border border-[#D2E0E6] text-[#47616B] px-3 py-2 rounded-md text-sm hover:bg-[#E0EBEF] hover:text-[#0B2830] transition-colors">
                <FileText className="w-4 h-4" /> Report
              </button>
              {isSuper && (
                <button data-testid="open-users" onClick={() => setShowUsers(true)}
                  className="flex items-center gap-1.5 bg-white border border-[#D2E0E6] text-[#47616B] px-3 py-2 rounded-md text-sm hover:bg-[#E0EBEF] hover:text-[#0B2830] transition-colors">
                  <Users className="w-4 h-4" /> Users
                </button>
              )}
              {isSuper && (
                <button data-testid="open-share" onClick={() => setShowShare(true)}
                  className="flex items-center gap-1.5 bg-white border border-[#D2E0E6] text-[#47616B] px-3 py-2 rounded-md text-sm hover:bg-[#E0EBEF] hover:text-[#0B2830] transition-colors">
                  <Share2 className="w-4 h-4" /> Publish
                </button>
              )}
              {isSuper && (
                <button data-testid="open-email" onClick={() => requireAuth(() => setShowEmail(true))}
                  className="flex items-center gap-1.5 bg-white border border-[#D2E0E6] text-[#47616B] px-3 py-2 rounded-md text-sm hover:bg-[#E0EBEF] hover:text-[#0B2830] transition-colors">
                  <Mail className="w-4 h-4" /> Daily Email
                </button>
              )}
              <button data-testid="refresh-btn" onClick={handleRefresh}
                className="flex items-center gap-1.5 bg-[#0284C7] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#0369A1] transition-colors">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
            </div>
          </header>

          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard testid="stat-matches" label="Companies shown" accent="#0284C7"
              value={rows.length} sub={`${data?.qualified_count ?? 0} fully qualify / ${data?.count ?? 0} screened`} />
            <StatCard testid="stat-avg-growth" label="Avg EBITDA YoY" accent="#059669"
              value={stats ? fmtPct(stats.avgYoy) : "\u2014"} sub="across shown names" />
            <StatCard testid="stat-buybacks" label="Total 12M buybacks" accent="#D97706"
              value={stats ? fmtM(stats.totalBuyback) : "\u2014"} sub="sum, USD" />
            <StatCard testid="stat-beats" label="Beating peers" accent="#0D9488"
              value={stats ? `${stats.beats}/${rows.length}` : "\u2014"} sub="below-median P/E + above-median growth" />
          </div>

          <div className="mb-6">
            <FilterBar filters={filters} setFilters={setFilters} sectors={data?.sectors || []}
              search={search} setSearch={setSearch} qualifiedOnly={qualifiedOnly} setQualifiedOnly={setQualifiedOnly}
              period={period} setPeriod={setPeriod} onApply={onApply} dirty={dirty} />
          </div>

          <SectorHeatmap
            stocks={data?.stocks || []}
            activeSector={filters.sector}
            onSelect={(sec) => setFilters((f) => ({ ...f, sector: f.sector === sec ? "" : sec }))}
          />

          <StockTable stocks={rows} sortKey={sortKey} sortDir={sortDir} onSort={onSort} onRowClick={setDetail} visibleColumns={visibleColumns} />

          <p className="text-xs text-[#7C949C] mt-4 max-w-3xl leading-relaxed">
            For informational purposes only, not investment advice. Financials sourced from free public data (stockanalysis.com);
            foreign-currency figures converted to USD at current FX. "Beats peers" compares each company to its own sector.
            A <span className="text-[#E11D48] font-medium">red &#9650; integrity flag</span> marks companies with publicly reported
            accounting concerns, shareholder securities-fraud litigation, or active regulatory investigations &mdash; these are
            allegations only, not a determination of wrongdoing. Click any row for the price chart, full KPIs & report.
          </p>

          <ScreenerInfo stats={visitorStats} />
        </>
      )}

      {showEmail && <EmailPanel token={token} onClose={() => setShowEmail(false)} />}
      {showUsers && <UsersPanel token={token} onClose={() => setShowUsers(false)} />}
      {showShare && <SharePanel token={token} onClose={() => setShowShare(false)} onRefresh={handleRefresh} refreshing={refreshing} />}
      {detail && <StockDetail stock={detail} token={token} period={applied.period} onClose={() => setDetail(null)} />}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} onAuthed={handleAuthed} />}
      {showChangePw && <ChangePasswordModal token={token} onClose={() => setShowChangePw(false)} />}
      {resetToken && <ResetPasswordModal resetToken={resetToken} onClose={() => setResetToken(null)} />}
    </div>
  );
}

export default App;
